import React, { useState } from "react";

const AddPersonForm = ({
  isOpen,
  onClose,
  onAdd,
  sectionType,
  initialData = null,
  existingPeople = [],
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [formData, setFormData] = useState({
    // Common fields
    firstName: initialData?.firstName || "",
    middleName: initialData?.middleName || "",
    lastName: initialData?.lastName || "",
    addressLine1: initialData?.addressLine1 || "",
    addressLine2: initialData?.addressLine2 || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    zip: initialData?.zip || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",

    // Person-specific fields (sellers, buyers, agents)
    livesOutsideUS: initialData?.livesOutsideUS || false,
    isCompanyOrPartnership: initialData?.isCompanyOrPartnership || false,

    // Lender-specific fields
    companyName: initialData?.companyName || "",
    loanNumber: initialData?.loanNumber || "",
    fax: initialData?.fax || "",

    // Agent-specific fields
    agentType: initialData?.agentType || "",
    licenseNumber: initialData?.licenseNumber || "",

    ...initialData,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear template selection when user manually changes fields
    if (selectedTemplate && field !== "selectedTemplate") {
      setSelectedTemplate("");
    }
  };

  const handleTemplateSelection = personId => {
    setSelectedTemplate(personId);
    if (personId === "") {
      // Reset to initial data if clearing selection
      setFormData({
        firstName: initialData?.firstName || "",
        middleName: initialData?.middleName || "",
        lastName: initialData?.lastName || "",
        addressLine1: initialData?.addressLine1 || "",
        addressLine2: initialData?.addressLine2 || "",
        city: initialData?.city || "",
        state: initialData?.state || "",
        zip: initialData?.zip || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        livesOutsideUS: initialData?.livesOutsideUS || false,
        isCompanyOrPartnership: initialData?.isCompanyOrPartnership || false,
        companyName: initialData?.companyName || "",
        loanNumber: initialData?.loanNumber || "",
        fax: initialData?.fax || "",
        agentType: initialData?.agentType || "",
        licenseNumber: initialData?.licenseNumber || "",
        ...initialData,
      });
    } else {
      // Auto-populate with selected person's data
      const selectedPerson = existingPeople.find(
        person => person.id.toString() === personId,
      );
      if (selectedPerson) {
        setFormData({
          ...selectedPerson,
          // Preserve the original ID if editing
          id: initialData?.id || selectedPerson.id,
        });
      }
    }
  };

  // Filter existing people to exclude the person being edited (if any)
  const availableTemplates = existingPeople.filter(
    person => !initialData || person.id !== initialData.id,
  );

  const handleSubmit = e => {
    if (e) e.preventDefault(); // Prevent default if called from form submit
    const newPerson = {
      id: initialData?.id || Date.now(),
      ...formData,
      displayName:
        `${formData.firstName} ${formData.lastName}`.trim() || "Unnamed Person",
    };
    onAdd(newPerson);
    // Don't call onClose() here - let the parent component handle closing
  };

  const handleCancel = () => {
    onClose();
  };

  const getSectionTitle = () => {
    const titles = {
      sellers: "Seller/Owner",
      buyers: "Buyer/Borrower",
      lenders: "Lender",
      realEstateAgents: "Real Estate Agent",
    };
    return titles[sectionType] || "Person";
  };

  const renderTemplateDropdown = () => {
    if (availableTemplates.length === 0) return null;

    return (
      <>
        <div className="form-order-services__column">
          <label className="sc-form-item sc-form-item--select">
            <span className="sc-form-item__label">
              Information is the same as
            </span>
            <div className="select-form-item__input-wrapper">
              <select
                className="sc-form-item__field"
                value={selectedTemplate}
                onChange={e => handleTemplateSelection(e.target.value)}
              >
                <option value="">Contact</option>
                {availableTemplates.map(person => {
                  const displayName =
                    `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
                    "Unnamed Person";
                  return (
                    <option key={person.id} value={person.id.toString()}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          </label>
        </div>
      </>
    );
  };

  const renderLenderForm = () => (
    <>
      {renderTemplateDropdown()}

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Company Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={e => handleInputChange("companyName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Loan Number</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="loanNumber"
              value={formData.loanNumber}
              onChange={e => handleInputChange("loanNumber", e.target.value)}
            />
          </label>
        </div>
      </div>

      <h3 className="add-person-form__section-title">Broker Info</h3>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">First Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={e => handleInputChange("firstName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Last Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={e => handleInputChange("lastName", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Address 1</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={e => handleInputChange("addressLine1", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">City</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="city"
              value={formData.city}
              onChange={e => handleInputChange("city", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item sc-form-item--select">
            <span className="sc-form-item__label">State/Province</span>
            <div className="select-form-item__input-wrapper">
              <select
                className="sc-form-item__field"
                name="state"
                value={formData.state}
                onChange={e => handleInputChange("state", e.target.value)}
              >
                <option value="">Select</option>
                <option value="AL">Alabama</option>
                <option value="AK">Alaska</option>
                <option value="AZ">Arizona</option>
                <option value="AR">Arkansas</option>
                <option value="CA">California</option>
                <option value="CO">Colorado</option>
                <option value="CT">Connecticut</option>
                <option value="DE">Delaware</option>
                <option value="FL">Florida</option>
                <option value="GA">Georgia</option>
                <option value="HI">Hawaii</option>
                <option value="ID">Idaho</option>
                <option value="IL">Illinois</option>
                <option value="IN">Indiana</option>
                <option value="IA">Iowa</option>
                <option value="KS">Kansas</option>
                <option value="KY">Kentucky</option>
                <option value="LA">Louisiana</option>
                <option value="ME">Maine</option>
                <option value="MD">Maryland</option>
                <option value="MA">Massachusetts</option>
                <option value="MI">Michigan</option>
                <option value="MN">Minnesota</option>
                <option value="MS">Mississippi</option>
                <option value="MO">Missouri</option>
                <option value="MT">Montana</option>
                <option value="NE">Nebraska</option>
                <option value="NV">Nevada</option>
                <option value="NH">New Hampshire</option>
                <option value="NJ">New Jersey</option>
                <option value="NM">New Mexico</option>
                <option value="NY">New York</option>
                <option value="NC">North Carolina</option>
                <option value="ND">North Dakota</option>
                <option value="OH">Ohio</option>
                <option value="OK">Oklahoma</option>
                <option value="OR">Oregon</option>
                <option value="PA">Pennsylvania</option>
                <option value="RI">Rhode Island</option>
                <option value="SC">South Carolina</option>
                <option value="SD">South Dakota</option>
                <option value="TN">Tennessee</option>
                <option value="TX">Texas</option>
                <option value="UT">Utah</option>
                <option value="VT">Vermont</option>
                <option value="VA">Virginia</option>
                <option value="WA">Washington</option>
                <option value="WV">West Virginia</option>
                <option value="WI">Wisconsin</option>
                <option value="WY">Wyoming</option>
              </select>
            </div>
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Zip</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="zip"
              value={formData.zip}
              onChange={e => handleInputChange("zip", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Phone</span>
            <input
              className="sc-form-item__field"
              type="tel"
              name="phone"
              value={formData.phone}
              placeholder="eg. 999-999-9999"
              onChange={e => handleInputChange("phone", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Fax</span>
            <input
              className="sc-form-item__field"
              type="tel"
              name="fax"
              value={formData.fax}
              placeholder="eg. 999-999-9999"
              onChange={e => handleInputChange("fax", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Email</span>
            <input
              className="sc-form-item__field"
              type="email"
              name="email"
              value={formData.email}
              onChange={e => handleInputChange("email", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          {/* Empty column for layout */}
        </div>
      </div>
    </>
  );

  const renderAgentForm = () => (
    <>
      {renderTemplateDropdown()}

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <fieldset className="form-order-services__fieldset">
            <legend className="form-order-services__legend">Agent Type</legend>
            <div className="form-order-services__radio-group">
              <label className="form-order-services__radio-option">
                <input
                  type="radio"
                  name="agentType"
                  value="listing"
                  checked={formData.agentType === "listing"}
                  onChange={e => handleInputChange("agentType", e.target.value)}
                />
                <span>Listing Agent</span>
              </label>
              <label className="form-order-services__radio-option">
                <input
                  type="radio"
                  name="agentType"
                  value="selling"
                  checked={formData.agentType === "selling"}
                  onChange={e => handleInputChange("agentType", e.target.value)}
                />
                <span>Selling Agent</span>
              </label>
              <label className="form-order-services__radio-option">
                <input
                  type="radio"
                  name="agentType"
                  value="both"
                  checked={formData.agentType === "both"}
                  onChange={e => handleInputChange("agentType", e.target.value)}
                />
                <span>Both</span>
              </label>
            </div>
          </fieldset>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Company Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={e => handleInputChange("companyName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">First Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={e => handleInputChange("firstName", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Last Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={e => handleInputChange("lastName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">License Number</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={e => handleInputChange("licenseNumber", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Email</span>
            <input
              className="sc-form-item__field"
              type="email"
              name="email"
              value={formData.email}
              onChange={e => handleInputChange("email", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Phone</span>
            <input
              className="sc-form-item__field"
              type="tel"
              name="phone"
              value={formData.phone}
              placeholder="eg. 999-999-9999"
              onChange={e => handleInputChange("phone", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Fax</span>
            <input
              className="sc-form-item__field"
              type="tel"
              name="fax"
              value={formData.fax}
              placeholder="eg. 999-999-9999"
              onChange={e => handleInputChange("fax", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          {/* Empty column for layout */}
        </div>
      </div>
    </>
  );

  const renderPersonForm = () => (
    <>
      <div className="form-order-services__column-container">
        <div className="form-order-services__column">
          <label className="form-order-services__checkbox-option">
            <input
              type="checkbox"
              checked={formData.livesOutsideUS}
              onChange={e =>
                handleInputChange("livesOutsideUS", e.target.checked)
              }
            />
            <span>Seller lives outside the U.S.</span>
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="form-order-services__checkbox-option">
            <input
              type="checkbox"
              checked={formData.isCompanyOrPartnership}
              onChange={e =>
                handleInputChange("isCompanyOrPartnership", e.target.checked)
              }
            />
            <span>Seller is a company or partnership</span>
          </label>
        </div>
      </div>

      <div className="form-order-services__column-container">
        {renderTemplateDropdown()}
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">First Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={e => handleInputChange("firstName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Middle Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="middleName"
              value={formData.middleName}
              onChange={e => handleInputChange("middleName", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Last Name</span>
            <input
              className="sc-form-item__field"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={e => handleInputChange("lastName", e.target.value)}
            />
          </label>
        </div>
        {!formData.livesOutsideUS && (
          <div className="form-order-services__column">
            <label className="sc-form-item">
              <span className="sc-form-item__label">Address Line 1</span>
              <input
                className="sc-form-item__field"
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={e =>
                  handleInputChange("addressLine1", e.target.value)
                }
              />
            </label>
          </div>
        )}

        {!formData.livesOutsideUS && (
          <>
            <div className="form-order-services__column">
              <label className="sc-form-item">
                <span className="sc-form-item__label">Address Line 2</span>
                <input
                  className="sc-form-item__field"
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={e =>
                    handleInputChange("addressLine2", e.target.value)
                  }
                />
              </label>
            </div>
            <div className="form-order-services__column">
              <label className="sc-form-item">
                <span className="sc-form-item__label">City</span>
                <input
                  className="sc-form-item__field"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={e => handleInputChange("city", e.target.value)}
                />
              </label>
            </div>
            <div className="form-order-services__column">
              <label className="sc-form-item sc-form-item--select">
                <span className="sc-form-item__label">State/Province</span>
                <div className="select-form-item__input-wrapper">
                  <select
                    className="sc-form-item__field"
                    name="state"
                    value={formData.state}
                    onChange={e => handleInputChange("state", e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                </div>
              </label>
            </div>
            <div className="form-order-services__column">
              <label className="sc-form-item">
                <span className="sc-form-item__label">Zip</span>
                <input
                  className="sc-form-item__field"
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={e => handleInputChange("zip", e.target.value)}
                />
              </label>
            </div>
          </>
        )}

        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Email</span>
            <input
              className="sc-form-item__field"
              type="email"
              name="email"
              value={formData.email}
              onChange={e => handleInputChange("email", e.target.value)}
            />
          </label>
        </div>
        <div className="form-order-services__column">
          <label className="sc-form-item">
            <span className="sc-form-item__label">Phone</span>
            <input
              className="sc-form-item__field"
              type="tel"
              name="phone"
              value={formData.phone}
              placeholder="eg. 999-999-9999"
              onChange={e => handleInputChange("phone", e.target.value)}
            />
          </label>
        </div>
      </div>
    </>
  );

  return (
    <div className="add-person-form">
      <div className="add-person-form__header">
        <h2 className="add-person-form__title">
          {initialData
            ? `Edit ${getSectionTitle()}`
            : `Add a ${getSectionTitle()}`}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="add-person-form__form">
        {sectionType === "lenders"
          ? renderLenderForm()
          : sectionType === "realEstateAgents"
            ? renderAgentForm()
            : renderPersonForm()}

        <div className="add-person-form__actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary add-person-form__submit-button"
            onClick={handleSubmit}
          >
            {initialData ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPersonForm;
